import React, { useState, useEffect } from 'react';
import { Brain, TrendingUp, Target, MessageCircle, BarChart3, RefreshCw, AlertCircle, CheckCircle2, Zap, ArrowRight, Star } from 'lucide-react';
import { 
  analyzeFunnelPerformance,
  scoreLeadsWithAI,
  generateFunnelOptimization,
  getNextBestActions,
  generateConversationStarters,
  generateSalesClosingStrategy
} from '../../../services/aiSalesFunnel.js';
import { isOpenAIAvailable, isQuotaExceeded } from '../../../services/openai.js';
import Icon from '../../../components/AppIcon';


const AISalesFunnelPanel = ({ leads, onLeadUpdate, onActionTaken }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('analysis');
  const [funnelAnalysis, setFunnelAnalysis] = useState(null);
  const [leadScores, setLeadScores] = useState(null);
  const [optimization, setOptimization] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [nextActions, setNextActions] = useState(null);
  const [conversationStarters, setConversationStarters] = useState(null);
  const [closingStrategy, setClosingStrategy] = useState(null);
  const [error, setError] = useState(null);

  // Mock funnel data for analysis
  const mockFunnelData = {
    total_leads: leads?.length || 0,
    stages: {
      new: leads?.filter(l => l?.status === 'new')?.length || 0,
      contacted: leads?.filter(l => l?.status === 'contacted')?.length || 0,
      qualified: leads?.filter(l => l?.status === 'qualified')?.length || 0,
      proposal: leads?.filter(l => l?.status === 'proposal')?.length || 0,
      negotiation: leads?.filter(l => l?.status === 'negotiation')?.length || 0,
      'closed-won': leads?.filter(l => l?.status === 'closed-won')?.length || 0,
      'closed-lost': leads?.filter(l => l?.status === 'closed-lost')?.length || 0
    },
    conversion_rates: {
      'new-to-contacted': 0.75,
      'contacted-to-qualified': 0.45,
      'qualified-to-proposal': 0.60,
      'proposal-to-negotiation': 0.30,
      'negotiation-to-closed': 0.70
    }
  };

  const tabs = [
    { id: 'analysis', label: 'Funnel Analysis', icon: BarChart3 },
    { id: 'scoring', label: 'Lead Scoring', icon: Target },
    { id: 'optimization', label: 'Optimization', icon: TrendingUp },
    { id: 'actions', label: 'Next Actions', icon: Zap },
    { id: 'conversation', label: 'Conversation AI', icon: MessageCircle }
  ];

  useEffect(() => {
    if (activeTab === 'analysis' && !funnelAnalysis) {
      handleFunnelAnalysis();
    }
  }, [activeTab]);

  const handleFunnelAnalysis = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const analysis = await analyzeFunnelPerformance(mockFunnelData, leads);
      setFunnelAnalysis(analysis);
    } catch (err) {
      setError('Failed to analyze funnel performance');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLeadScoring = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const scoring = await scoreLeadsWithAI(leads, {
        demographic_weight: 0.3,
        behavioral_weight: 0.4,
        engagement_weight: 0.3
      });
      setLeadScores(scoring);
    } catch (err) {
      setError('Failed to score leads with AI');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOptimization = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const optimizationData = await generateFunnelOptimization(mockFunnelData, {
        historical_performance: mockFunnelData?.conversion_rates,
        industry: 'technology',
        target_improvement: 0.20
      });
      setOptimization(optimizationData);
    } catch (err) {
      setError('Failed to generate optimization recommendations');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNextActions = async (lead) => {
    setIsLoading(true);
    setError(null);
    setSelectedLead(lead);
    try {
      const actions = await getNextBestActions(lead, [
        { type: 'email', date: new Date(Date.now() - 86400000)?.toISOString(), response: true },
        { type: 'call', date: new Date(Date.now() - 172800000)?.toISOString(), response: false }
      ]);
      setNextActions(actions);
    } catch (err) {
      setError('Failed to generate next actions');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConversationStarters = async (lead) => {
    setIsLoading(true);
    setError(null);
    setSelectedLead(lead);
    try {
      const starters = await generateConversationStarters(lead, 'sales_call');
      setConversationStarters(starters);
    } catch (err) {
      setError('Failed to generate conversation starters');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClosingStrategy = async (lead) => {
    setIsLoading(true);
    setError(null);
    setSelectedLead(lead);
    try {
      const strategy = await generateSalesClosingStrategy(lead, {
        sales_cycle_stage: lead?.status || 'qualification',
        product_interest: 'high',
        budget_qualified: true
      });
      setClosingStrategy(strategy);
    } catch (err) {
      setError('Failed to generate closing strategy');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'Critical': 'text-red-600 bg-red-50',
      'High': 'text-orange-600 bg-orange-50',
      'Medium': 'text-yellow-600 bg-yellow-50',
      'Low': 'text-gray-600 bg-gray-50'
    };
    return colors?.[priority] || colors?.['Medium'];
  };

  const getHealthColor = (health) => {
    const colors = {
      'Excellent': 'text-green-600',
      'Good': 'text-blue-600',
      'Average': 'text-yellow-600',
      'Poor': 'text-orange-600',
      'Critical': 'text-red-600'
    };
    return colors?.[health] || colors?.['Average'];
  };

  const renderAPIStatus = () => {
    if (!isOpenAIAvailable()) {
      return (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
          <div className="flex items-center space-x-2 text-orange-700">
            <AlertCircle className="w-5 h-5" />
            <span className="font-medium">OpenAI API Not Configured</span>
          </div>
          <p className="text-sm text-orange-600 mt-1">
            Add your OpenAI API key to environment variables to enable AI insights.
          </p>
        </div>
      );
    }

    if (isQuotaExceeded()) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <div className="flex items-center space-x-2 text-red-700">
            <AlertCircle className="w-5 h-5" />
            <span className="font-medium">API Quota Exceeded</span>
          </div>
          <p className="text-sm text-red-600 mt-1">
            Your OpenAI API quota has been exceeded. Please upgrade your plan for full AI functionality.
          </p>
        </div>
      );
    }

    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
        <div className="flex items-center space-x-2 text-green-700">
          <CheckCircle2 className="w-5 h-5" />
          <span className="font-medium">AI Ready</span>
        </div>
        <p className="text-sm text-green-600 mt-1">
          OpenAI integration is active and ready to provide insights.
        </p>
      </div>
    );
  };

  const renderFunnelAnalysis = () => (
    <div className="space-y-6">
      {/* Overall Health */}
      {funnelAnalysis && (
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Funnel Health Overview</h3>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getHealthColor(funnelAnalysis?.overall_health)}`}>
              {funnelAnalysis?.overall_health}
            </span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-foreground">
                {(funnelAnalysis?.conversion_rate?.current * 100)?.toFixed(1)}%
              </div>
              <div className="text-sm text-muted-foreground">Current Conversion</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {(funnelAnalysis?.conversion_rate?.benchmark * 100)?.toFixed(1)}%
              </div>
              <div className="text-sm text-muted-foreground">Industry Benchmark</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {(funnelAnalysis?.conversion_rate?.improvement_potential * 100)?.toFixed(1)}%
              </div>
              <div className="text-sm text-muted-foreground">Potential Improvement</div>
            </div>
          </div>

          {/* Bottlenecks */}
          <div className="mb-6">
            <h4 className="text-md font-medium text-foreground mb-3">Identified Bottlenecks</h4>
            <div className="space-y-3">
              {funnelAnalysis?.bottlenecks?.map((bottleneck, index) => (
                <div key={index} className="bg-muted rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-foreground">{bottleneck?.stage}</span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityColor(bottleneck?.impact)}`}>
                      {bottleneck?.impact} Impact
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{bottleneck?.issue}</p>
                  <p className="text-sm text-blue-600 font-medium">💡 {bottleneck?.solution}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Lead Insights */}
          <div>
            <h4 className="text-md font-medium text-foreground mb-3">Lead Insights</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="text-sm font-medium text-muted-foreground mb-2">High-Value Segments</div>
                <ul className="text-sm space-y-1">
                  {funnelAnalysis?.lead_insights?.high_value_segments?.map((segment, index) => (
                    <li key={index} className="flex items-center space-x-2">
                      <Star className="w-3 h-3 text-yellow-500" />
                      <span>{segment}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-sm font-medium text-muted-foreground mb-2">Behavioral Patterns</div>
                <ul className="text-sm space-y-1">
                  {funnelAnalysis?.lead_insights?.behavioral_patterns?.map((pattern, index) => (
                    <li key={index} className="flex items-center space-x-2">
                      <TrendingUp className="w-3 h-3 text-blue-500" />
                      <span>{pattern}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-sm font-medium text-muted-foreground mb-2">Conversion Predictors</div>
                <ul className="text-sm space-y-1">
                  {funnelAnalysis?.lead_insights?.conversion_predictors?.map((predictor, index) => (
                    <li key={index} className="flex items-center space-x-2">
                      <Target className="w-3 h-3 text-green-500" />
                      <span>{predictor}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      <button
        onClick={handleFunnelAnalysis}
        disabled={isLoading}
        className="w-full bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center space-x-2"
      >
        {isLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Brain className="w-4 h-4" />}
        <span>{isLoading ? 'Analyzing...' : 'Analyze Funnel'}</span>
      </button>
    </div>
  );

  const renderLeadScoring = () => (
    <div className="space-y-6">
      {leadScores && (
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">AI Lead Scoring Results</h3>
            <div className="text-sm text-muted-foreground">
              {leadScores?.scoring_summary?.high_priority_count} high-priority leads
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-foreground">
                {leadScores?.scoring_summary?.average_score?.toFixed(0) || 0}
              </div>
              <div className="text-sm text-muted-foreground">Average Score</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {leadScores?.scored_leads?.filter(l => l?.priority_level === 'High')?.length || 0}
              </div>
              <div className="text-sm text-muted-foreground">High Priority</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {((leadScores?.scored_leads?.filter(l => l?.conversion_probability > 0.6)?.length || 0) / 
                  (leadScores?.scored_leads?.length || 1) * 100)?.toFixed(0)}%
              </div>
              <div className="text-sm text-muted-foreground">High Conversion Prob.</div>
            </div>
          </div>

          <div className="space-y-3 max-h-80 overflow-y-auto">
            {leadScores?.scored_leads?.slice(0, 10)?.map((scoredLead, index) => {
              const originalLead = leads?.find(l => l?.id?.toString() === scoredLead?.lead_id);
              return (
                <div key={index} className="bg-muted rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <img
                        src={originalLead?.avatar || `https://api.dicebear.com/7.x/initials/svg?seed=${originalLead?.name}`}
                        alt={originalLead?.name}
                        className="w-8 h-8 rounded-full"
                      />
                      <div>
                        <div className="font-medium text-foreground">{originalLead?.name}</div>
                        <div className="text-sm text-muted-foreground">{originalLead?.company}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-foreground">{scoredLead?.ai_score}</div>
                      <div className={`px-2 py-1 rounded text-xs font-medium ${getPriorityColor(scoredLead?.priority_level)}`}>
                        {scoredLead?.priority_level}
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    <div className="text-center">
                      <div className="text-sm font-medium">{scoredLead?.score_factors?.demographic}</div>
                      <div className="text-xs text-muted-foreground">Demo</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-medium">{scoredLead?.score_factors?.behavioral}</div>
                      <div className="text-xs text-muted-foreground">Behavior</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-medium">{scoredLead?.score_factors?.engagement}</div>
                      <div className="text-xs text-muted-foreground">Engage</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-medium">{scoredLead?.score_factors?.fit}</div>
                      <div className="text-xs text-muted-foreground">Fit</div>
                    </div>
                  </div>

                  <div className="text-sm text-muted-foreground mb-2">
                    <strong>Key Insights:</strong> {scoredLead?.key_insights?.join(' • ')}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {scoredLead?.recommended_actions?.map((action, actionIndex) => (
                      <button
                        key={actionIndex}
                        onClick={() => {
                          onActionTaken?.(originalLead, action);
                        }}
                        className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded hover:bg-blue-100"
                      >
                        {action?.action} • {action?.channel}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <button
        onClick={handleLeadScoring}
        disabled={isLoading}
        className="w-full bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center space-x-2"
      >
        {isLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Target className="w-4 h-4" />}
        <span>{isLoading ? 'Scoring Leads...' : 'Score Leads with AI'}</span>
      </button>
    </div>
  );

  const renderOptimization = () => (
    <div className="space-y-6">
      {optimization && (
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Optimization Strategy</h3>
          
          {/* Strategy Overview */}
          <div className="bg-muted rounded-lg p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-sm font-medium text-muted-foreground mb-1">Focus Areas</div>
                <ul className="text-sm space-y-1">
                  {optimization?.optimization_strategy?.focus_areas?.map((area, index) => (
                    <li key={index} className="flex items-center space-x-2">
                      <ArrowRight className="w-3 h-3 text-blue-500" />
                      <span>{area}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-sm font-medium text-muted-foreground mb-1">Expected Improvement</div>
                <div className="text-lg font-bold text-green-600">
                  {optimization?.optimization_strategy?.expected_improvement}
                </div>
                <div className="text-sm text-muted-foreground">
                  Timeline: {optimization?.optimization_strategy?.implementation_timeline}
                </div>
              </div>
            </div>
          </div>

          {/* Stage Optimizations */}
          <div className="mb-6">
            <h4 className="text-md font-medium text-foreground mb-3">Stage-by-Stage Optimizations</h4>
            <div className="space-y-3">
              {optimization?.stage_optimizations?.map((stage, index) => (
                <div key={index} className="bg-muted rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-foreground">{stage?.stage_name}</span>
                    <div className="text-sm">
                      <span className="text-muted-foreground">Current: {stage?.current_conversion}</span>
                      <span className="mx-2">→</span>
                      <span className="text-green-600 font-medium">Target: {stage?.target_conversion}</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {stage?.optimization_tactics?.map((tactic, tacticIndex) => (
                      <div key={tacticIndex} className="flex items-center justify-between text-sm">
                        <span>{tactic?.tactic}</span>
                        <div className="flex space-x-2">
                          <span className={`px-2 py-1 rounded text-xs ${
                            tactic?.implementation_effort === 'Low' ? 'bg-green-100 text-green-600' :
                            tactic?.implementation_effort === 'Medium'? 'bg-yellow-100 text-yellow-600' : 'bg-red-100 text-red-600'
                          }`}>
                            {tactic?.implementation_effort} Effort
                          </span>
                          <span className={`px-2 py-1 rounded text-xs ${
                            tactic?.expected_impact === 'High' ? 'bg-blue-100 text-blue-600' :
                            tactic?.expected_impact === 'Medium'? 'bg-gray-100 text-gray-600' : 'bg-gray-100 text-gray-500'
                          }`}>
                            {tactic?.expected_impact} Impact
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Automation Opportunities */}
          <div>
            <h4 className="text-md font-medium text-foreground mb-3">Automation Opportunities</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {optimization?.automation_opportunities?.map((opportunity, index) => (
                <div key={index} className="bg-muted rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-foreground">{opportunity?.process}</span>
                    <span className={`px-2 py-1 rounded text-xs ${
                      opportunity?.implementation_complexity === 'Simple' ? 'bg-green-100 text-green-600' :
                      opportunity?.implementation_complexity === 'Moderate'? 'bg-yellow-100 text-yellow-600' : 'bg-red-100 text-red-600'
                    }`}>
                      {opportunity?.implementation_complexity}
                    </span>
                  </div>
                  <div className="text-sm text-muted-foreground mb-2">{opportunity?.automation_type}</div>
                  <ul className="text-sm space-y-1">
                    {opportunity?.benefits?.map((benefit, benefitIndex) => (
                      <li key={benefitIndex} className="flex items-center space-x-2">
                        <CheckCircle2 className="w-3 h-3 text-green-500" />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <button
        onClick={handleOptimization}
        disabled={isLoading}
        className="w-full bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center space-x-2"
      >
        {isLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <TrendingUp className="w-4 h-4" />}
        <span>{isLoading ? 'Generating...' : 'Generate Optimization Strategy'}</span>
      </button>
    </div>
  );

  const renderNextActions = () => (
    <div className="space-y-6">
      {/* Lead Selection */}
      <div className="bg-card border border-border rounded-lg p-4">
        <h3 className="text-md font-medium text-foreground mb-3">Select a Lead for AI Recommendations</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
          {leads?.slice(0, 10)?.map((lead) => (
            <button
              key={lead?.id}
              onClick={() => handleNextActions(lead)}
              disabled={isLoading}
              className={`p-3 rounded-lg border text-left hover:bg-muted transition-colors ${
                selectedLead?.id === lead?.id ? 'border-primary bg-primary/10' : 'border-border'
              }`}
            >
              <div className="flex items-center space-x-3">
                <img
                  src={lead?.avatar || `https://api.dicebear.com/7.x/initials/svg?seed=${lead?.name}`}
                  alt={lead?.name}
                  className="w-8 h-8 rounded-full"
                />
                <div>
                  <div className="font-medium text-foreground">{lead?.name}</div>
                  <div className="text-sm text-muted-foreground">{lead?.company}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Next Actions Results */}
      {nextActions && selectedLead && (
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center space-x-3 mb-4">
            <img
              src={selectedLead?.avatar || `https://api.dicebear.com/7.x/initials/svg?seed=${selectedLead?.name}`}
              alt={selectedLead?.name}
              className="w-10 h-10 rounded-full"
            />
            <div>
              <h3 className="text-lg font-semibold text-foreground">{selectedLead?.name}</h3>
              <div className="text-sm text-muted-foreground">{selectedLead?.company}</div>
            </div>
          </div>

          {/* Lead Status */}
          <div className="bg-muted rounded-lg p-4 mb-4">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-sm text-muted-foreground">Current Stage</div>
                <div className="font-medium">{nextActions?.lead_status?.current_stage}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Engagement</div>
                <div className="font-medium">{nextActions?.lead_status?.engagement_level}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Readiness</div>
                <div className="font-medium">{nextActions?.lead_status?.conversion_readiness}</div>
              </div>
            </div>
          </div>

          {/* Recommended Actions */}
          <div className="mb-4">
            <h4 className="text-md font-medium text-foreground mb-3">Recommended Actions</h4>
            <div className="space-y-3">
              {nextActions?.recommended_actions?.map((action, index) => (
                <div key={index} className="bg-muted rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-foreground">{action?.action_type}</span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityColor(action?.priority)}`}>
                      {action?.priority}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{action?.description}</p>
                  <div className="flex items-center justify-between text-sm">
                    <span><strong>Channel:</strong> {action?.channel}</span>
                    <span><strong>Timing:</strong> {action?.timing}</span>
                  </div>
                  <div className="mt-2">
                    <div className="text-sm font-medium text-muted-foreground mb-1">Expected Outcome:</div>
                    <div className="text-sm">{action?.expected_outcome}</div>
                  </div>
                  <button
                    onClick={() => {
                      onActionTaken?.(selectedLead, action);
                    }}
                    className="mt-2 w-full bg-blue-50 text-blue-600 px-3 py-2 rounded hover:bg-blue-100 text-sm font-medium"
                  >
                    Take Action
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Risk Factors */}
          {nextActions?.risk_factors?.length > 0 && (
            <div>
              <h4 className="text-md font-medium text-foreground mb-3">Risk Factors</h4>
              <div className="space-y-2">
                {nextActions?.risk_factors?.map((risk, index) => (
                  <div key={index} className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                    <div className="flex items-start space-x-2">
                      <AlertCircle className="w-4 h-4 text-orange-500 mt-0.5" />
                      <div>
                        <div className="text-sm font-medium text-orange-700">{risk?.risk}</div>
                        <div className="text-sm text-orange-600">{risk?.mitigation}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderConversationAI = () => (
    <div className="space-y-6">
      {/* Lead Selection */}
      <div className="bg-card border border-border rounded-lg p-4">
        <h3 className="text-md font-medium text-foreground mb-3">Select Lead for Conversation Assistance</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
          {leads?.slice(0, 10)?.map((lead) => (
            <div key={lead?.id} className="space-y-2">
              <button
                onClick={() => handleConversationStarters(lead)}
                disabled={isLoading}
                className={`w-full p-3 rounded-lg border text-left hover:bg-muted transition-colors ${
                  selectedLead?.id === lead?.id ? 'border-primary bg-primary/10' : 'border-border'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <img
                    src={lead?.avatar || `https://api.dicebear.com/7.x/initials/svg?seed=${lead?.name}`}
                    alt={lead?.name}
                    className="w-8 h-8 rounded-full"
                  />
                  <div>
                    <div className="font-medium text-foreground">{lead?.name}</div>
                    <div className="text-sm text-muted-foreground">{lead?.company}</div>
                  </div>
                </div>
              </button>
              <button
                onClick={() => handleClosingStrategy(lead)}
                disabled={isLoading}
                className="w-full bg-green-50 text-green-600 px-3 py-2 rounded hover:bg-green-100 text-sm font-medium"
              >
                Generate Closing Strategy
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Conversation Starters */}
      {conversationStarters && selectedLead && (
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Conversation Starters for {selectedLead?.name}</h3>
          
          <div className="space-y-4">
            {conversationStarters?.conversation_starters?.map((starter, index) => (
              <div key={index} className="bg-muted rounded-lg p-4">
                <div className="mb-3">
                  <div className="text-sm text-muted-foreground mb-1">Opening Line:</div>
                  <div className="font-medium text-foreground bg-card p-3 rounded border-l-4 border-blue-500">
                    "{starter?.opener}"
                  </div>
                </div>
                
                <div className="mb-3">
                  <div className="text-sm text-muted-foreground mb-2">Follow-up Questions:</div>
                  <ul className="space-y-1">
                    {starter?.follow_up_questions?.map((question, qIndex) => (
                      <li key={qIndex} className="text-sm bg-card p-2 rounded">
                        • {question}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="text-xs text-muted-foreground">
                  <strong>Context:</strong> {starter?.context}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Closing Strategy */}
      {closingStrategy && selectedLead && (
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Closing Strategy for {selectedLead?.name}</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-foreground">{closingStrategy?.lead_score}</div>
              <div className="text-sm text-muted-foreground">Lead Score</div>
            </div>
            <div className="text-center">
              <div className={`text-lg font-bold ${
                closingStrategy?.closing_probability === 'High' ? 'text-green-600' :
                closingStrategy?.closing_probability === 'Medium'? 'text-yellow-600' : 'text-red-600'
              }`}>
                {closingStrategy?.closing_probability}
              </div>
              <div className="text-sm text-muted-foreground">Closing Probability</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-blue-600">
                {closingStrategy?.recommended_techniques?.length || 0}
              </div>
              <div className="text-sm text-muted-foreground">Techniques</div>
            </div>
          </div>

          {/* Recommended Techniques */}
          <div className="mb-6">
            <h4 className="text-md font-medium text-foreground mb-3">Recommended Closing Techniques</h4>
            <div className="space-y-3">
              {closingStrategy?.recommended_techniques?.map((technique, index) => (
                <div key={index} className="bg-muted rounded-lg p-4">
                  <div className="font-medium text-foreground mb-2">{technique?.technique}</div>
                  <p className="text-sm text-muted-foreground mb-2">{technique?.description}</p>
                  <div className="text-sm mb-2">
                    <strong>When to use:</strong> {technique?.when_to_use}
                  </div>
                  <div className="bg-card p-3 rounded border-l-4 border-green-500">
                    <div className="text-sm font-medium text-muted-foreground mb-1">Script Example:</div>
                    <div className="text-sm italic">"{technique?.script_example}"</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Next Actions */}
          <div>
            <h4 className="text-md font-medium text-foreground mb-3">Next Actions</h4>
            <div className="space-y-2">
              {closingStrategy?.next_actions?.map((action, index) => (
                <div key={index} className="flex items-center justify-between bg-muted rounded-lg p-3">
                  <span className="text-sm">{action?.action}</span>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityColor(action?.priority)}`}>
                      {action?.priority}
                    </span>
                    <span className="text-xs text-muted-foreground">{action?.timeline}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-1">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center space-x-2 mb-2">
          <Brain className="w-6 h-6 text-primary" />
          <h2 className="text-lg font-semibold text-foreground">AI Sales Funnel</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          AI-powered insights and automation for your sales funnel optimization
        </p>
      </div>
      <div className="p-4">
        {/* API Status */}
        {renderAPIStatus()}

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-center space-x-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              <span className="font-medium">Error</span>
            </div>
            <p className="text-sm text-red-600 mt-1">{error}</p>
          </div>
        )}

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-border">
          {tabs?.map((tab) => {
            const Icon = tab?.icon;
            return (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                  activeTab === tab?.id
                    ? 'bg-primary text-primary-foreground border-b-2 border-primary'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab?.label}</span>
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="min-h-[400px]">
          {activeTab === 'analysis' && renderFunnelAnalysis()}
          {activeTab === 'scoring' && renderLeadScoring()}
          {activeTab === 'optimization' && renderOptimization()}
          {activeTab === 'actions' && renderNextActions()}
          {activeTab === 'conversation' && renderConversationAI()}
        </div>
      </div>
    </div>
  );
};

export default AISalesFunnelPanel;